import React, { useState } from 'react';
import { useSiteContent } from '../context/SiteContentContext';

const Pricing = () => {
  const { siteContent } = useSiteContent();
  const [activeTab, setActiveTab] = useState('Web Design');

  const tabs = [
    "Web Design", "SMM", "SEO", "2D / 3D Animation", 
    "Content Marketing", "E-Commerce", "Video Marketing", "Logo Design"
  ];

  const defaultPricingData = {
    "Web Design": [
      {
        title: "Startup Website Package", price: "$150.00 USD", features: [
          "Up to 10 fully customizable pages tailored to your business needs.",
          "Mobile-friendly responsive design for seamless viewing on all devices.",
          "Basic SEO setup to enhance visibility on search engines.",
          "Optimized for fast loading speeds to improve user experience.",
          "Easy-to-use contact form integration for capturing leads.",
          "One-time setup fee of 100 USD with no hidden charges."
        ], popular: false
      },
      {
        title: "Professional Website Package", price: "$250.00 USD", features: [
          "Unlimited pages (up to 50) to showcase your business and services.",
          "Advanced responsive design for seamless adaptability on all devices.",
          "Comprehensive SEO optimization to improve rankings and visibility.",
          "Fast and secure website with advanced performance measures.",
          "Custom contact forms and lead capture to efficiently gather inquiries.",
          "Priority support and maintenance to ensure smooth website operation."
        ], popular: true
      },
      {
        title: "Custom Website Package", price: "Inquire Now", features: [
          "Fully tailored design to meet your unique business requirements.",
          "Unlimited pages with no restriction on the number of pages.",
          "Advanced responsive design for an optimal experience on all devices.",
          "Comprehensive SEO strategies for maximum online visibility.",
          "Custom features and integrations to enhance functionality.",
          "Ongoing support and maintenance for continuous performance."
        ], popular: false
      }
    ],
    "SMM": [
      {
        title: "SMM Startup", price: "$50.00 USD", features: [
          "Customized social media strategies aligned with your business goals.",
          "Optimization of social media profiles for better visibility.",
          "Creation of engaging and relevant content.",
          "Active engagement with your audience to build relationships.",
          "Regular performance reports and analytics.",
          "Competitor analysis to stay ahead in the industry."
        ], popular: false
      },
      {
        title: "Pro Video Editing", price: "$250.00 USD", features: [
          "High-quality video editing tailored to your brand.",
          "Captivating visuals to enhance video storytelling.",
          "Optimization for different social media platforms.",
          "Strategic placement of call-to-actions.",
          "Quick turnaround time for timely delivery.",
          "Revisions based on your feedback for perfect results."
        ], popular: true
      },
      {
        title: "Custom SMM Package", price: "Inquire Now", features: [
          "Fully customized solutions tailored to your specific business needs.",
          "Unlimited options for features, integrations, and functionalities.",
          "Advanced design and development to create a unique user experience.",
          "Comprehensive SEO and performance optimization for maximum impact.",
          "Flexible and scalable to grow with your business.",
          "Dedicated support and maintenance for seamless operation."
        ], popular: false
      }
    ],
    "SEO": [
      {
        title: "Startup SEO Packages", price: "$150.00 USD", features: [
          "Basic SEO setup to enhance your website’s visibility.",
          "Keyword research to target the right audience.",
          "On-page optimization for improved search engine rankings.",
          "Submission of your site to major search engines.",
          "Performance tracking with basic analytics.",
          "Affordable solution ideal for new businesses."
        ], popular: false
      },
      {
        title: "Professional SEO Packages", price: "$250.00 USD", features: [
          "Comprehensive SEO audit to identify areas for improvement.",
          "Advanced keyword strategy for better search engine targeting.",
          "Technical SEO improvements for enhanced website performance.",
          "High-quality backlink building to increase domain authority.",
          "Detailed analytics and reporting for tracking SEO success.",
          "Ongoing optimization to maintain and improve rankings."
        ], popular: true
      },
      {
        title: "Custom SEO Packages", price: "Inquire Now", features: [
          "Fully tailored SEO strategy based on your business goals.",
          "Customized keyword research and targeting for niche markets.",
          "Advanced on-page and off-page SEO techniques.",
          "Integration of custom features and tools to enhance SEO efforts.",
          "Regular performance monitoring and strategy adjustments.",
          "Dedicated support to address specific SEO needs and challenges."
        ], popular: false
      }
    ],
    "2D / 3D Animation": [
      {
        title: "Basic Illustrative", price: "$100.00 USD", features: [
          "Creative and engaging 2D animations tailored to your brand.",
          "Storyboarding and character design to bring your ideas to life.",
          "Smooth, high-quality animations for a professional look.",
          "Suitable for explainer videos, marketing, and educational content.",
          "Quick turnaround times to meet project deadlines.",
          "Revisions included to ensure your satisfaction with the final product."
        ], popular: false
      },
      {
        title: "Startup Illustrative", price: "$200.00 USD", features: [
          "High-quality 3D animations for a realistic and dynamic experience.",
          "Detailed modeling and texturing to create visually stunning content.",
          "Advanced lighting and effects for an immersive viewer experience.",
          "Ideal for product demos, advertisements, and entertainment.",
          "Custom animations tailored to your specific needs and vision.",
          "Ongoing support and revisions to perfect the final output."
        ], popular: true
      },
      {
        title: "Custom Illustrative", price: "Inquire Now", features: [
          "Unique and personalized illustrations to match your brand’s identity.",
          "Hand-drawn or digital illustrations tailored to your project.",
          "Suitable for branding, marketing, and storytelling purposes.",
          "High-resolution artwork for both print and digital use.",
          "Collaborative process with revisions to ensure satisfaction.",
          "Quick delivery while maintaining high-quality standards."
        ], popular: false
      }
    ],
    "Content Marketing": [
      {
        title: "Web Content Basic", price: "$99.00 USD", features: [
          "Concise and effective content for key web pages like Home and About.",
          "SEO-optimized content for better online visibility.",
          "Clear messaging to communicate your brand’s values.",
          "User-friendly structure for easy navigation.",
          "Quick turnaround for fast website setup.",
          "Basic content designed to establish your online presence."
        ], popular: false
      },
      {
        title: "Web Content Starter", price: "$300.00 USD", features: [
          "A complete set of essential web pages for your website.",
          "SEO-driven content for higher search engine rankings.",
          "Engaging and informative writing to capture visitors’ attention.",
          "Tailored content to match your business objectives and audience.",
          "Optimized for both readability and user experience.",
          "Suitable for businesses looking to establish a strong online presence."
        ], popular: true
      },
      {
        title: "Custom Web Content", price: "Inquire Now", features: [
          "Fully customized content designed to meet your specific needs.",
          "In-depth SEO strategies to maximize visibility and traffic.",
          "Content crafted to resonate with your target audience.",
          "Tailored messaging that reflects your brand’s personality.",
          "High-quality writing for product descriptions, landing pages, and more.",
          "Ongoing support and updates to keep your content fresh."
        ], popular: false
      }
    ],
    "E-Commerce": [
      {
        title: "Basic E-Commerce Package", price: "$150.00 USD", features: [
          "Setup of a simple online store with essential features.",
          "User-friendly interface for easy product management.",
          "Integration of basic payment gateways for secure transactions.",
          "Mobile-responsive design for optimal viewing on all devices.",
          "Basic SEO setup to help your store rank in search engines.",
          "One-time setup with no hidden charges."
        ], popular: false
      },
      {
        title: "Startup E-Commerce Package", price: "$200.00 USD", features: [
          "Fully customizable online store tailored to your business needs.",
          "Inventory management system to track stock and sales.",
          "Integration of multiple payment options for customer convenience.",
          "Responsive design for seamless shopping across devices.",
          "Basic SEO and marketing tools to drive traffic to your store.",
          "Customer support for store setup and initial troubleshooting."
        ], popular: true
      },
      {
        title: "Professional E-Commerce Package", price: "$250.00 USD", features: [
          "Advanced ecommerce solutions with unlimited product listings.",
          "Integration of advanced payment gateways and shipping options.",
          "Custom design and features for a unique shopping experience.",
          "Comprehensive SEO strategies to maximize online visibility.",
          "Advanced analytics and reporting tools for performance tracking.",
          "Ongoing support and maintenance for continuous store performance."
        ], popular: false
      }
    ],
    "Video Marketing": [
      {
        title: "Video Production", price: "$49.00 USD", features: [
          "Per Ride 0 up to 10 mins.",
          "High-definition video quality for a professional look.",
          "Scriptwriting and storyboarding for engaging narratives.",
          "Advanced editing with color correction and sound design.",
          "Integration of branded elements like logos and graphics.",
          "Fast turnaround to meet tight deadlines."
        ], popular: true 
      }
    ],
    "Logo Design": [
      {
        title: "For Startups", price: "$25.00 USD", features: [
          "3 Logo Design Concepts", "3 Revisions", "1 Dedicated Designer",
          "Free Color Options", "Free Grayscale Format", "24 To 48 Hours TAT",
          "All File Formats (PNG, JPG)", "You Own All Design Rights",
          "100% Money Back Guarantee"
        ], popular: false
      },
      {
        title: "For Entrepreneur", price: "$55.00 USD", features: [
          "4 Custom Logo Design Concepts by 2 Designers", "Unlimited Revisions",
          "24 to 48 hours TAT", "100% Ownership Rights", "100% Satisfaction Guarantee",
          "100% Unique Design Guarantee", "100% Money Back Guarantee*"
        ], popular: true
      },
      {
        title: "For Small Businesses", price: "$105.00 USD", features: [
          "12 Logo Design Concepts", "4 Designers", "48 Hours TAT",
          "6 Design Revisions", "Dedicated Account Manager",
          "You Own All Design Rights", "100% Money Back Guarantee"
        ], popular: false
      }
    ]
  };

  const activeCards = (siteContent?.pricing?.[activeTab] && Array.isArray(siteContent.pricing[activeTab]))
    ? siteContent.pricing[activeTab]
    : (defaultPricingData[activeTab] || []);

  return (
    <section className="w-full py-8 md:py-28 bg-transparent relative z-10 border-none">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="text-center mb-8 md:mb-16">
          <div className="inline-flex items-center gap-2 bg-[#F3CA52]/10 border border-[#F3CA52]/20 px-4 py-1.5 rounded-full mb-4">
            <span className="w-2 h-2 rounded-full bg-[#F3CA52] animate-pulse" />
            <span className="text-xs font-black text-[#F3CA52] uppercase tracking-[0.2em]">
              Our Pricing Plans
            </span>
          </div>
          <p className="text-4xl sm:text-6xl font-black text-white tracking-tight mb-4">
            Affordable Plans For <span className="text-[#F3CA52] relative inline-block">Your Business</span>
          </p>
          <p className="max-w-2xl mx-auto text-zinc-400 text-base sm:text-lg">
            Select a service tab below to discover tailored packages built specifically to amplify your digital impact.
          </p>
        </div>

        {/* 🚀 Premium 8-Tabs — Mobile Single Line Horizontal Scroll */}
        <div className="w-full mb-8 md:mb-20 -mx-4 md:mx-0">
          <div className="flex flex-nowrap overflow-x-auto gap-2 px-4 pb-2 md:flex-wrap md:justify-center md:overflow-visible md:px-0 md:pb-0 scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-none whitespace-nowrap px-4 py-2.5 rounded-lg border transition-all duration-300 font-black text-[10px] sm:text-xs uppercase tracking-widest ${
                  activeTab === tab
                    ? 'btn-tab-active border-transparent'
                    : 'bg-transparent border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-white'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* 📦 Dynamic Cards Engine */}
        <div className={`grid gap-8 items-stretch justify-center ${
          activeCards.length === 1 
            ? 'grid-cols-1 max-w-md mx-auto' 
            : 'grid-cols-1 md:grid-cols-3'
        }`}>
          {activeCards.map((card, index) => (
            <div
              key={index}
              className={`group relative rounded-3xl p-8 bg-zinc-950/30 backdrop-blur-md border flex flex-col justify-between transition-all duration-500 transform hover:-translate-y-3 ${
                card.popular
                  ? 'border-[#F3CA52] shadow-[0_0_50px_rgba(243,202,82,0.08)]'
                  : 'border-zinc-800/60 hover:border-zinc-600'
              }`}
            >
              {card.popular && (
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#F3CA52] to-[#d4aa2e] text-black text-[10px] font-black tracking-[0.2em] uppercase px-4 py-1 rounded-full shadow-md">
                  Best Seller
                </span>
              )}

              <div>
                <h3 className="text-xl font-extrabold text-white mb-3 tracking-wide group-hover:text-[#F3CA52] transition-colors duration-300">
                  {card.title}
                </h3>
                
                <div className="flex items-baseline mb-6 border-b border-zinc-900/60 pb-5">
                  <span className={`tracking-tight font-black ${
                    card.price?.includes('$') ? 'text-4xl text-white' : 'text-3xl text-[#F3CA52]'
                  }`}>
                    {card.price}
                  </span>
                </div>

                <ul className="space-y-4">
                  {(card.features || []).map((feature, idx) => (
                    <li key={idx} className="flex items-start text-sm text-zinc-400 gap-3 group-hover:text-zinc-200 transition-colors duration-300">
                      <svg
                        className="w-4 h-4 text-[#F3CA52] shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                      </svg>
                      <span className="leading-relaxed">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Action Interactive CTAs - UPDATED */}
              <div className="mt-8 pt-4 space-y-3">
                <button className="btn-animated-gold w-full">
                  Order Now
                </button>
                
                <a
                  href="tel:+15122544193"
                  className="flex items-center justify-center gap-2 text-xs font-bold text-zinc-500 hover:text-[#F3CA52] transition-colors duration-300 py-1"
                >
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 004.582 4.582l.774-1.548a1 1 0 011.06-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                  +1 (512) 254-4193
                </a>
              </div>

            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default Pricing;